import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, HardHat, User, ArrowRight } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

// Build option nodes imperatively to avoid build-time JSX annotators
// that inject <span> children into <option> elements (React hydration error).
const renderStateOptions = () => {
  const opts = [React.createElement('option', { key: '_', value: '' }, '—')];
  for (const [code, name] of US_STATES) {
    opts.push(React.createElement('option', { key: code, value: code }, `${code} — ${name}`));
  }
  return opts;
};

const AuthPage = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const initialRole = searchParams.get('role') === 'contractor' ? 'contractor' : 'client';
  const [role, setRole] = useState(initialRole);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    state: '',
    zip_code: '',
    license_number: '',
    terms_accepted: false,
    sms_consent: false,
    marketing_consent: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isLogin) {
      if (!formData.terms_accepted) {
        setError(t('auth.errTerms'));
        return;
      }
      if (!formData.sms_consent) {
        setError(t('auth.errSmsConsent'));
        return;
      }
    }

    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        navigateByRole(user.role);
      } else {
        const user = await register({
          ...formData,
          role: role
        });
        navigateByRole(user.role);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    // Persist desired role so the callback page can attach it when creating a new user
    if (!isLogin) {
      localStorage.setItem('clab_oauth_role', role);
    } else {
      localStorage.removeItem('clab_oauth_role');
    }
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const navigateByRole = (userRole) => {
    if (userRole === 'contractor') {
      navigate('/contractor/dashboard');
    } else if (userRole === 'client') {
      navigate('/client/dashboard');
    } else if (userRole === 'admin') {
      navigate('/admin/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <SEO
        title={isLogin ? 'Sign In' : 'Create Account'}
        description="Sign in or create your ContractorsLab account. Clients post jobs for free; contractors join our performance-based marketplace."
        path="/auth"
        noindex={!isLogin}
      />
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
        {/* Construction team photo — smooth-masked to fade right edge */}
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_friendly-meninsky-5/artifacts/0zdj0s2m_iStock-2259013963.jpg')",
            backgroundSize: 'auto 110%',
            backgroundPosition: 'left top',
            backgroundRepeat: 'no-repeat',
            filter: 'grayscale(35%) contrast(1.02)',
            mixBlendMode: 'luminosity',
            WebkitMaskImage:
              'linear-gradient(90deg, #000 0%, rgba(0,0,0,0.9) 45%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.15) 88%, transparent 100%)',
            maskImage:
              'linear-gradient(90deg, #000 0%, rgba(0,0,0,0.9) 45%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.15) 88%, transparent 100%)',
          }}
          aria-hidden
        />
        {/* Smooth dark gradient overlay for text readability */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.85) 40%, rgba(15,23,42,0.95) 100%)',
          }}
          aria-hidden
        />
        {/* Orange accent glow bottom-right */}
        <div
          className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(234,88,12,0.28) 0%, transparent 65%)' }}
          aria-hidden
        />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-orange-600 rounded-sm flex items-center justify-center">
              <HardHat className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-black text-white tracking-tight">CONTRACTORSLAB</span>
          </Link>
          
          <h1 className="text-5xl font-black text-white leading-tight mb-6">
            WHERE PROS<br />GET WORK DONE
          </h1>
          
          <p className="text-xl text-slate-300 mb-8 max-w-md">
            The performance-based marketplace connecting quality contractors with serious clients.
          </p>
          
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 bg-orange-600/20 rounded flex items-center justify-center">
                <span className="text-orange-500 font-bold">1</span>
              </div>
              <span>Pay only when you win the job</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 bg-orange-600/20 rounded flex items-center justify-center">
                <span className="text-orange-500 font-bold">2</span>
              </div>
              <span>Verified licensed contractors only</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 bg-orange-600/20 rounded flex items-center justify-center">
                <span className="text-orange-500 font-bold">3</span>
              </div>
              <span>No contact info until you approve</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher variant="light" />
        </div>
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
              <HardHat className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-black text-slate-900 tracking-tight">CONTRACTORSLAB</span>
          </Link>

          <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-8">
            <h2 className="text-2xl font-black text-slate-900 mb-2">
              {isLogin ? t('auth.login').toUpperCase() : t('auth.register').toUpperCase()}
            </h2>
            <p className="text-slate-600 mb-6">
              {isLogin ? 'Welcome back! Enter your credentials.' : 'Join the marketplace today.'}
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6 text-sm">
                {error}
              </div>
            )}

            {!isLogin && (
              <div className="mb-6">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  {t('auth.iAmA')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRole('client')}
                    className={`p-4 rounded-sm border-2 transition-all ${
                      role === 'client' 
                        ? 'border-orange-600 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-client-btn"
                  >
                    <User className={`w-6 h-6 mx-auto mb-2 ${role === 'client' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <span className={`block text-sm font-bold ${role === 'client' ? 'text-orange-600' : 'text-slate-600'}`}>
                      {t('auth.client')}
                    </span>
                    <span className="block text-xs text-slate-500 mt-1">Looking for contractors</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('contractor')}
                    className={`p-4 rounded-sm border-2 transition-all ${
                      role === 'contractor' 
                        ? 'border-orange-600 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-contractor-btn"
                  >
                    <HardHat className={`w-6 h-6 mx-auto mb-2 ${role === 'contractor' ? 'text-orange-600' : 'text-slate-400'}`} />
                    <span className={`block text-sm font-bold ${role === 'contractor' ? 'text-orange-600' : 'text-slate-600'}`}>
                      {t('auth.contractor')}
                    </span>
                    <span className="block text-xs text-slate-500 mt-1">Looking for work</span>
                  </button>
                </div>
              </div>
            )}

            {/* Google OAuth Button (Emergent-managed) */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 border-2 border-slate-300 hover:border-slate-900 text-slate-900 font-bold py-3.5 rounded-sm transition-colors mb-4"
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.continueWithGoogle')}
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs uppercase text-slate-400 font-bold">{t('common.or')}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent pr-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {isLogin && (
                  <div className="mt-2 text-right">
                    <Link
                      to="/forgot-password"
                      className="text-xs font-semibold text-slate-500 hover:text-orange-600 transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                    {t('auth.phone')} {role === 'contractor' ? <span className="text-orange-600">*</span> : <span className="text-slate-400 normal-case">({t('auth.optional')})</span>}
                  </label>
                  <input
                    type="tel"
                    required={role === 'contractor'}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                    data-testid="phone-input"
                  />
                </div>
              )}

              {!isLogin && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                    {t('auth.address')} <span className="text-slate-400 normal-case">({t('auth.optional')})</span>
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St"
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                    data-testid="address-input"
                  />
                </div>
              )}

              {!isLogin && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      {t('auth.state')} <span className="text-orange-600">*</span>
                    </label>
                    <select
                      required
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent bg-white"
                      data-testid="state-input"
                    >
                      {renderStateOptions()}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      {t('auth.zipCode')} <span className="text-orange-600">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      inputMode="numeric"
                      pattern="[0-9]{5}"
                      maxLength={5}
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                      placeholder="90210"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                      data-testid="zip-input"
                    />
                  </div>
                </div>
              )}

              {!isLogin && role === 'contractor' && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                    {t('auth.licenseNumber')} <span className="text-orange-600">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                    placeholder="e.g. CSLB #123456"
                    className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent"
                    data-testid="license-number-input"
                  />
                  <p className="mt-1.5 text-xs text-slate-500">
                    {t('auth.licenseHelp')}
                  </p>
                </div>
              )}

              {!isLogin && (
                <div className="pt-2 space-y-3 border-t border-slate-200 mt-2">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      required
                      checked={formData.terms_accepted}
                      onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-orange-600 cursor-pointer flex-shrink-0"
                      data-testid="consent-terms-checkbox"
                    />
                    <span className="text-xs text-slate-700 leading-relaxed">
                      {t('auth.consentTermsPrefix')}{' '}
                      <Link to="/terms" target="_blank" className="text-orange-600 font-semibold underline hover:text-orange-700">
                        {t('auth.consentTermsLink')}
                      </Link>{' '}
                      {t('common.and')}{' '}
                      <Link to="/privacy" target="_blank" className="text-orange-600 font-semibold underline hover:text-orange-700">
                        {t('auth.consentPrivacyLink')}
                      </Link>
                      <span className="text-orange-600">*</span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      required
                      checked={formData.sms_consent}
                      onChange={(e) => setFormData({ ...formData, sms_consent: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-orange-600 cursor-pointer flex-shrink-0"
                      data-testid="consent-sms-checkbox"
                    />
                    <span className="text-xs text-slate-700 leading-relaxed">
                      {t('auth.consentTransactional')}
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.marketing_consent}
                      onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-orange-600 cursor-pointer flex-shrink-0"
                      data-testid="consent-marketing-checkbox"
                    />
                    <span className="text-xs text-slate-700 leading-relaxed">
                      {t('auth.consentMarketing')}{' '}
                      <span className="text-slate-400">({t('auth.optional')})</span>
                    </span>
                  </label>

                  <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
                    <span className="text-orange-600">*</span> {t('auth.smsDisclaimer1')}{' '}
                    <a href="mailto:support@contractorslab.com" className="underline hover:text-orange-600">support@contractorslab.com</a>. {t('auth.smsDisclaimer2')}{' '}
                    <Link to="/terms" target="_blank" className="underline hover:text-orange-600">{t('auth.consentTermsLink')}</Link>,{' '}
                    <Link to="/privacy" target="_blank" className="underline hover:text-orange-600">{t('auth.consentPrivacyLink')}</Link>, {t('common.and')}{' '}
                    <Link to="/sms-terms" target="_blank" className="underline hover:text-orange-600">{t('auth.consentSmsTermsLink')}</Link>.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide py-4 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-btn"
              >
                {loading ? t('common.loading') : (isLogin ? t('auth.login') : t('auth.register'))}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  const nextIsLogin = !isLogin;
                  setIsLogin(nextIsLogin);
                  navigate(nextIsLogin ? '/auth' : '/register');
                }}
                className="text-slate-600 hover:text-orange-600 text-sm"
                data-testid="toggle-auth-btn"
              >
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <span className="font-bold">{isLogin ? 'Sign Up' : 'Sign In'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
